import _startCase from "lodash/startCase";
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { STICKY_TOTAL_ROW_HEIGHT, VirtualizedTable } from 'core/components/table';
import { getDimensionDisplayLabel, getDimensions } from '@kentik/ui-shared/query/query';
import { getMeasurementDimension } from '@kentik/ui-shared/query/measurements';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import { observer } from 'mobx-react';
import { renderDimensionValue, renderMetricValue } from './legendUtils';
import UdeRowActionsMenu from './UdeRowActionsMenu';
import SeriesColorLine from './SeriesColorLine';
const ROW_HEIGHT = 30;
export function getUdeTableHeight(rowCount, {
  showTotalRow
}) {
  return ROW_HEIGHT + rowCount * ROW_HEIGHT + (showTotalRow ? STICKY_TOTAL_ROW_HEIGHT : 0);
}
const Wrapper = styled.div.withConfig({
  displayName: "UdeTable__Wrapper",
  componentId: "sc-darhtj-0"
})(["&&{display:flex;flex-direction:column;flex:1 0 100%;overflow:auto hidden;.thead .th{white-space:nowrap;text-overflow:ellipsis;> *{min-width:0;overflow:hidden;text-overflow:ellipsis;}}}"]);

/**
 * Indicates whether a row is rendered on the chart: the coloured line keeps the series colour at all
 * times and signals selection through line weight and alpha.
 */
const SeriesColor = observer(({
  model
}) => {
  const {
    queryModel,
    queryRequest
  } = model.results;
  const isCharted = queryRequest.isRowCharted(model.id);
  return /*#__PURE__*/React.createElement(SeriesColorLine, {
    color: isCharted ? queryModel.getSeriesColor(model.id) : undefined,
    isSelected: queryModel.isSeriesVisible(model.id)
  });
});
function getRowClass(model) {
  const {
    queryModel,
    queryRequest
  } = model.results;
  const isTableOnly = queryModel.settings.chart.type === 'table';
  const isVisible = !isTableOnly && queryModel.isSeriesVisible(model.id);
  const isCharted = !isTableOnly && queryRequest.isRowCharted(model.id);
  const isDisabled = !isTableOnly && !queryRequest.isRowCharted(model.id);
  return classNames({
    isTableOnly,
    isVisible,
    isCharted,
    [Classes.DISABLED]: isDisabled
  });
}
function getColumns(queryRequest, measurement, rollups, onAddRowFilters) {
  const {
    query,
    settings
  } = queryRequest;
  const isTableOnly = settings.chart.type === 'table';
  const position = isTableOnly ? 'bottom' : settings.chart.legend?.position || 'bottom';
  const showRollups = isTableOnly || (settings?.chart?.legend?.showRollups ?? true);
  const cols = [];
  if (!isTableOnly) {
    cols.push({
      name: 'color',
      label: '',
      width: position === 'right' ? 28 : 36,
      padding: '6px',
      ellipsis: false,
      sortable: false,
      resizable: false,
      colSpan: (_, totalRow) => totalRow ? 2 : 1,
      renderer: ({
        model
      }) => /*#__PURE__*/React.createElement(SeriesColor, {
        model: model
      })
    });
  }
  getDimensions(query).forEach(dimension => {
    const info = getMeasurementDimension(measurement, dimension.name);
    const baseLabel = info?.Label || _startCase(dimension.name);
    // Agent dimensions render a rich AgentDisplay (with its own hover popover); disable the
    // cell's native ellipsis title so it doesn't show the raw agent id tooltip.
    const isAgentDimension = dimension.name === 'src_agent' || dimension.name === 'dst_agent';
    cols.push({
      name: `dimensions.${dimension.name}`,
      label: getDimensionDisplayLabel(dimension.name, baseLabel),
      width: 200,
      sortable: false,
      paddingRight: 12,
      ellipsis: isAgentDimension ? false : undefined,
      renderer: ({
        model
      }) => renderDimensionValue(dimension.name, model.get(), query, measurement)
    });
  });
  if (showRollups) {
    rollups.forEach(rollup => {
      const baseLabel = rollup.metricInfo?.Label || rollup.metric;
      const metricLabel = rollup.metricInfo?.Window === 'count' ? getDimensionDisplayLabel(rollup.metric, baseLabel) : baseLabel;
      const rollupLabel = `${metricLabel} (${_startCase(rollup.fn)})`;
      const hasEnumValues = Boolean(rollup.metricInfo?.Values && Object.keys(rollup.metricInfo.Values).length > 0);
      cols.push({
        name: `rollups.${rollup.key}`,
        label: rollupLabel,
        width: 150,
        sortable: false,
        align: 'right',
        paddingRight: 12,
        showTotal: !hasEnumValues,
        totalRenderer: ({
          value
        }) => renderMetricValue(rollup.metric, value, {
          metricInfo: rollup.metricInfo,
          measurement: query.measurement,
          forcePrefix: rollup.prefix
        }),
        renderer: ({
          value
        }) => renderMetricValue(rollup.metric, value, {
          metricInfo: rollup.metricInfo,
          measurement: query.measurement,
          forcePrefix: rollup.prefix
        })
      });
    });
  }
  if (onAddRowFilters) {
    cols.push({
      name: 'rowActions',
      label: '',
      width: 36,
      padding: '0px',
      ellipsis: false,
      sortable: false,
      resizable: false,
      renderer: ({
        model
      }) => /*#__PURE__*/React.createElement("span", {
        "data-row-actions": true
      }, /*#__PURE__*/React.createElement(UdeRowActionsMenu, {
        row: model.get(),
        query: query,
        measurement: measurement,
        onAddFilters: onAddRowFilters
      }))
    });
  }
  return cols;
}
function UdeTable({
  queryRequest,
  measurement,
  rollups,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  onAddRowFilters
}) {
  const {
    queryModel,
    results,
    settings
  } = queryRequest;
  const isTableOnly = settings.chart.type === 'table';
  const columns = useMemo(() => getColumns(queryRequest, measurement, rollups, onAddRowFilters), [measurement, queryRequest, rollups, onAddRowFilters]);
  const handleRowClick = (model, evt) => {
    if (evt.target.closest('[data-row-actions]')) {
      return;
    }
    if (queryModel.isRowCharted(model.id)) {
      const rowIndex = results.rows.findIndex(r => r.id === model.id);
      onRowClick(model.id, rowIndex, evt);
    }
  };
  const handleRowMouseOver = model => {
    onRowMouseEnter(model.id);
  };
  const handleRowMouseOut = () => {
    onRowMouseLeave();
  };
  const showRollups = isTableOnly || (settings?.chart?.legend?.showRollups ?? true);
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(VirtualizedTable, {
    flexed: true,
    collection: results,
    columns: columns,
    tabPrefId: "udeTable",
    persistColumnWidths: false,
    rowHeight: ROW_HEIGHT,
    rowClass: getRowClass,
    selectOnRowClick: false,
    showTotalRow: showRollups,
    stickyTotalRow: showRollups,
    onRowClick: !isTableOnly ? handleRowClick : undefined,
    onRowMouseOver: !isTableOnly ? handleRowMouseOver : undefined,
    onRowMouseOut: !isTableOnly ? handleRowMouseOut : undefined
  }));
}
export default UdeTable;